import { AloftToolkitBlob, expected } from '@aloft/toolkit-shared';
import { BlobRef } from '@aloft/toolkit-shared/lib/blob/index';
import { useMutation } from '@tanstack/react-query';

import { ToolkitContext, useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../../hooks/api/uploadBlob.ts';

function storeOrSkip(
  f: File | undefined,
  ctx: ToolkitContext,
): Promise<AloftToolkitBlob.Type | undefined> {
  return f ? uploadBlob(f, f?.name, ctx) : Promise.resolve(undefined);
}

function blobResultToReference(input: AloftToolkitBlob.Type | undefined): BlobRef | undefined {
  if (input === undefined) return input;

  return {
    filename: expected(input.name),
    blobID: input.id,
  };
}

type UploadQAFilesParams = {
  reportPDF: File;
  orderDetailsPDF: File | undefined;
  loePDF: File | undefined;
  purchaseContractPDF: File | undefined;
};

type UploadQAFilesReturn = {
  reportPDF: BlobRef;
  orderDetailsPDF: BlobRef | undefined;
  loePDF: BlobRef | undefined;
  purchaseContractPDF: BlobRef | undefined;
};

export function useUploadQAFiles() {
  const ctx = useToolkitContext();

  const uploadQAFiles = async (params: UploadQAFilesParams): Promise<UploadQAFilesReturn> => {
    const { reportPDF, loePDF, purchaseContractPDF, orderDetailsPDF } = params;

    const [reportRes, orderDetailsRes, loeRes, purchaseContractRes] = await Promise.all([
      uploadBlob(reportPDF, reportPDF.name, ctx),
      storeOrSkip(orderDetailsPDF, ctx),
      storeOrSkip(loePDF, ctx),
      storeOrSkip(purchaseContractPDF, ctx),
    ]);

    return {
      reportPDF: { filename: expected(reportRes.name), blobID: reportRes.id },
      orderDetailsPDF: blobResultToReference(orderDetailsRes),
      loePDF: blobResultToReference(loeRes),
      purchaseContractPDF: blobResultToReference(purchaseContractRes),
    };
  };

  return useMutation({
    mutationFn: uploadQAFiles,
  });
}
